import { useCallback, useState } from 'react';
import type { FC } from 'react';

import { useTranslation } from 'react-i18next';

import { ParentalControlsHeader } from '../../shared/ParentalControlHeader/ParentalControlsHeader';
import { AgeButtons } from '../../shared/AgeButtons/AgeButtons';
import { PinInputs } from '../../shared/PinInputs/PinInputs';
import { Button } from '../../../Button/Button';
import { getCurrentPageViewData } from '../../../../services/tracking/helpers/pulseOrigin';
import { trackButtonClickEvent } from '../../../../services/tracking/pulseEvents';

import * as styles from './ActivateParentalControls.module.scss';

interface ActivateParentalControlsProps {
  handleClose: () => void;
  handleLock: (pinCode?: string, ageLimit?: number) => void;
}

const ActivateParentalControls: FC<ActivateParentalControlsProps> = ({
  handleClose,
  handleLock,
}) => {
  const [ageLimit, setAgeLimit] = useState<number | undefined>(undefined);
  const [pinCode, setPinCode] = useState<string | undefined>(undefined);

  const trackButtonClick = useCallback((ageLimit) => {
    const currentPageViewData = getCurrentPageViewData();

    const button = {
      elementType: 'AgeButton',
      title: ageLimit,
    };

    const targetUrl = currentPageViewData.url;

    const target = {
      elementType: 'ConfirmCode',
      name: 'ParentalControls',
      url: targetUrl,
    };

    const parent = {
      elementType: 'ActivateParentalControls',
      name: 'Select age limit',
    };

    trackButtonClickEvent(button, target, parent);
  }, []);

  const onLockClick = useCallback(() => {
    handleLock?.(pinCode, ageLimit);
    trackButtonClick(ageLimit);
  }, [ageLimit, pinCode, handleLock, trackButtonClick]);

  const allowNextStep = Boolean(ageLimit && pinCode && pinCode.length === 4);

  const { t } = useTranslation();

  return (
    <>
      <ParentalControlsHeader
        titleText={t('activate parental controls')}
        subtitleText={t(
          'you will need to enter this code to watch restricted content'
        )}
      />
      <AgeButtons onAgeButtonClick={setAgeLimit} />
      <div className={styles.label}>{t('choose your code')}</div>
      <PinInputs onPinInputEnter={setPinCode} />
      <div className={styles.buttons}>
        <Button
          variant="contained"
          size="medium"
          className={styles.button}
          onClick={handleClose}
        >
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          size="medium"
          active={allowNextStep}
          onClick={onLockClick}
          disabled={!allowNextStep}
        >
          {t('next')}
        </Button>
      </div>
    </>
  );
};

export { ActivateParentalControls };
